import { useIntl } from 'react-intl'

import { type SupportBlocking as SupportBlockingValue, type SupportRequesterStatus } from '@onr/schema'
import { MessageSquareIcon } from 'lucide-react'

import { EmptyState } from '@/components/atoms/EmptyState'
import { SupportBlocking } from '@/components/molecules/SupportBlocking'
import { SupportStatusPill } from '@/components/molecules/SupportStatusPill'
import { cn } from '@/lib/utils.ts'

export interface SupportRequestRow {
  readonly number: number
  readonly meta: string
  readonly title: string
  readonly requesterStatus: SupportRequesterStatus
  readonly blocking: SupportBlockingValue
  readonly activity: string
  readonly unread: boolean
}

export interface SupportRequestListProps {
  readonly rows: ReadonlyArray<SupportRequestRow>
  readonly onSelect: (number: number) => void
}

export const SupportRequestList = ({ rows, onSelect }: SupportRequestListProps) => {
  const intl = useIntl()

  if (rows.length === 0) {
    return (
      <EmptyState
        className="border-t border-gray-200"
        icon={<MessageSquareIcon aria-hidden className="size-8 stroke-[1.5] text-gray-500" />}
        message={intl.formatMessage({ id: 'soutien.requests.empty' })}
      />
    )
  }

  return (
    <nav
      aria-label={intl.formatMessage({ id: 'soutien.requests.list' })}
      className="flex-1 overflow-y-auto border-t border-gray-200">
      <ul>
        {rows.map((row) => (
          <li key={row.number}>
            <button
              type="button"
              onClick={() => onSelect(row.number)}
              className={cn(
                'grid w-full cursor-pointer grid-cols-[12px_minmax(0,1fr)] gap-2 border-b border-gray-200 py-4 pr-6 pl-3 text-left hover:bg-gray-50',
                row.unread && 'bg-blue-100 hover:bg-blue-100'
              )}>
              {row.unread ? (
                <span
                  role="img"
                  aria-label={intl.formatMessage({ id: 'soutien.requests.unread' })}
                  className="mt-1 size-2 rounded-full bg-blue-700"
                />
              ) : (
                <span />
              )}
              <span className="flex min-w-0 flex-col gap-1.5">
                <span className={cn('text-sm', row.unread ? 'text-gray-600' : 'text-gray-500')}>{row.meta}</span>
                <span className={cn('text-gray-900', row.unread && 'font-bold')}>{row.title}</span>
                <span className="flex flex-wrap items-center gap-2">
                  <SupportStatusPill status={row.requesterStatus} />
                  <SupportBlocking blocking={row.blocking} />
                  <span className={cn('text-sm', row.unread ? 'text-gray-700' : 'text-gray-500')}>{row.activity}</span>
                </span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </nav>
  )
}
